import {Component, computed, effect, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {UserProfileService} from './user-profile.service';
import {UserProfileDto} from '../../../models/generated-models';
import {formatUserRoleSummary} from '../../app.constants';
import {ThaiDateTimePipe} from '../../shared/commons/thai-datetime.pipe';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {AvatarCropModalComponent} from '../../shared/commons/avatar-crop-modal/avatar-crop-modal.component';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {environment} from '../../../environments/environment';
import {AccountSwitcherComponent} from '../../shared/commons/account-switcher/account-switcher.component';
import {HasAnyRoleDirective} from '../../shared/directives/has-any-role.directive';
import {CurrentUserContextService} from '../../shared/services/current-user-context.service';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ThaiDateTimePipe,
    PageTitleComponent,
    AccountSwitcherComponent,
    HasAnyRoleDirective,
  ],
  templateUrl: './user-profile.html',
  styleUrl: './user-profile.scss',
})
export class UserProfile implements OnInit {
  protected readonly currentUserProfile: WritableSignal<UserProfileDto | null> = signal(null);
  protected avatarUploading = false;
  protected avatarPreviewUrl: string | null = null;
  private readonly profileService = inject(UserProfileService);
  private readonly currentUserContext = inject(CurrentUserContextService);
  private readonly toast = inject(ToastService);
  private readonly modal = inject(NgbModal);
  private readonly confirmDialog = inject(ConfirmDialogService);

  constructor() {
    effect(() => {
      const profile = this.currentUserContext.profile();
      if (profile) {
        this.currentUserProfile.set(profile);
      }
    });
  }

  ngOnInit(): void {
    this.profileService.getPersonalProfile().subscribe({
      next: (profile) => this.currentUserProfile.set(profile),
      error: () => {
      }
    });
  }

  protected get displayAvatarUrl(): string | null {
    return this.avatarPreviewUrl ?? this.currentUserProfile()?.profileImageUrl ?? null;
  }

  protected onAvatarFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.toast.error('รองรับเฉพาะไฟล์รูปภาพเท่านั้น');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      this.toast.error('ขนาดไฟล์ต้องไม่เกิน 4MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageDataUrl = e.target?.result as string;
      const modalRef = this.modal.open(AvatarCropModalComponent, {size: 'sm', centered: true});
      modalRef.componentInstance.imageDataUrl = imageDataUrl;
      modalRef.componentInstance.outputSize = 512;
      modalRef.result.then((blob: Blob) => {
        this.uploadAvatar(new File([blob], file.name, {type: blob.type}));
      }).catch(() => {
      });
    };
    reader.readAsDataURL(file);
  }

  private uploadAvatar(file: File): void {
    const prev = this.avatarPreviewUrl;
    this.avatarPreviewUrl = URL.createObjectURL(file);
    this.avatarUploading = true;
    this.profileService.uploadProfileAvatar(file).subscribe({
      next: (updated) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        this.avatarPreviewUrl = null;
        this.currentUserProfile.set(updated);
        this.currentUserContext.refresh().subscribe({
          error: () => {}
        });
        this.avatarUploading = false;
        this.toast.success('อัปโหลดรูปโปรไฟล์เรียบร้อยแล้ว');
      },
      error: () => {
        if (this.avatarPreviewUrl) {
          URL.revokeObjectURL(this.avatarPreviewUrl);
        }
        this.avatarPreviewUrl = null;
        this.avatarUploading = false;
      }
    });
  }

  protected get thaiName(): string {
    const p = this.currentUserProfile();
    return [p?.prefixName, p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(' ') || '—';
  }

  protected get englishName(): string {
    const p = this.currentUserProfile();
    return [p?.prefixNameEn, p?.firstNameEn, p?.middleNameEn, p?.lastNameEn].filter(Boolean).join(' ') || '—';
  }

  protected readonly fullAddress = computed(() => {
    const p = this.currentUserProfile();
    if (!p) {
      return '—';
    }
    const parts = [
      p.addressNo && `เลขที่ ${p.addressNo}`,
      p.moo && `หมู่ที่ ${p.moo}`,
      p.soi && `ตรอก/ซอย ${p.soi}`,
      p.road && `ถนน ${p.road}`,
      p.subDistrictName && `ตำบล/แขวง ${p.subDistrictName}`,
      p.districtName && `อำเภอ/เขต ${p.districtName}`,
      p.provinceName && `จังหวัด ${p.provinceName}`,
      p.postCode && `รหัสไปรษณีย์ ${p.postCode}`,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
  });

  protected get cardTypeLabel(): string {
    return this.currentUserProfile()?.cardType === 'PASSPORT' ? 'หนังสือเดินทาง' : 'บัตรประจำตัวประชาชน';
  }

  protected get userRoleSummary(): string {
    return formatUserRoleSummary(this.currentUserProfile()?.userRoles) || '—';
  }

  protected redirectToPortalProfile(): void {
    this.confirmDialog.open({
      title: 'แก้ไขข้อมูลโปรไฟล์',
      content: '<div class="text-center">ระบบจะนำคุณไปยังหน้าโปรไฟล์ของ Portal เพื่อแก้ไขข้อมูล ต้องการดำเนินการต่อหรือไม่</div> ',
      isInnerHTML: true,
      confirmButtonMsg: 'ไปยัง Portal',
      closeButtonMsg: 'ยกเลิก',
    }).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      const returnUrl = window.location.href;
      const portalProfileUrl = new URL( environment.portalProfileUrl);
      portalProfileUrl.searchParams.set('redirectUrl', returnUrl);
      window.location.assign(portalProfileUrl.toString());
    });
  }

  protected copyCode(text: string): void {
    navigator.clipboard.writeText(text);
  }
}
